import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
  ButtonBase
} from '@mui/material';

import {
  CheckCircleOutline,
  AssignmentOutlined,
  ErrorOutline,
  SwapHorizOutlined,
  AccessTimeOutlined
} from '@mui/icons-material';
import { caseService } from '../services/api';
import CasesTable from './CasesTable';
import { useTranslation } from 'react-i18next';

const statusIcons = {
  Open: <AssignmentOutlined color="primary" />,
  Recommended: <CheckCircleOutline color="success" />,
  'Not Recommended': <ErrorOutline color="error" />,
  Processed: <CheckCircleOutline color="success" />,
  Reassigned: <SwapHorizOutlined color="warning" />,
  Expired: <ErrorOutline color="error" />
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const theme = useTheme();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseCounts = async () => {
      try {
        setLoading(true);
        const response = await caseService.getCaseCounts();
        console.log('Dashboard: Raw case counts response:', response);
        console.log('Dashboard: Case counts object:', response.data.counts);

        // Convert all status keys to lowercase for consistent access
        const countsData = response.data.counts;
        const normalizedCounts = {};

        // Process the counts to ensure all statuses are properly handled
        Object.keys(countsData).forEach(key => {
          // For 'Not Recommended', use 'not_recommended' as the key for consistent access
          if (key.toLowerCase() === 'not recommended') {
            normalizedCounts['not_recommended'] = countsData[key];
          } else {
            const lowerKey = key.toLowerCase();
            normalizedCounts[lowerKey] = countsData[key];
          }
        });

        console.log('Dashboard: Normalized counts:', normalizedCounts);
        setCounts(normalizedCounts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching case counts:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError('Failed to load dashboard data. Please check if the backend API is running.');
        setLoading(false);
      }
    };

    fetchCaseCounts();
  }, [i18n.language]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const statusCards = [
    {
      title: t('dashboard.open'),
      count: counts.open || 0,
      icon: statusIcons.Open,
      color: theme.palette.primary.main,
      status: 'Open',
      description: t('dashboard.newInspection')
    },
    {
      title: t('dashboard.processed'),
      count: counts.processed || 0,
      icon: statusIcons.Processed,
      color: theme.palette.success.main,
      status: 'Processed',
      description: t('dashboard.completedInspections')
    },
    {
      title: t('dashboard.reassigned'),
      count: counts.reassigned || 0,
      icon: statusIcons.Reassigned,
      color: theme.palette.warning.main,
      status: 'Reassigned',
      description: t('dashboard.reassignedToRecapture')
    },
    {
      title: t('dashboard.recommended'),
      count: counts.recommended || 0,
      icon: statusIcons.Recommended,
      color: theme.palette.success.main,
      status: 'Recommended',
      description: t('dashboard.approvedForProcessing')
    },
    {
      title: t('dashboard.rejected'),
      count: counts['not_recommended'] || 0,
      icon: statusIcons['Not Recommended'],
      color: theme.palette.error.main,
      status: 'Not Recommended',
      description: t('dashboard.rejectedInspection')
    },
    {
      title: t('dashboard.expired'),
      count: counts.expired || 0,
      icon: statusIcons.Expired,
      color: theme.palette.error.main,
      status: 'Expired',
      description: t('dashboard.pastDueInspections')
    }
  ];

  return (
    <Box sx={{ flexGrow: 1, maxWidth: 1440, mx: 'auto' }}>
      {/* Status Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
          lg: 'repeat(6, minmax(0, 1fr))'
        },
        alignItems: 'stretch',
        gap: 2,
        mb: 3,
        pb: 1
      }}>
        {statusCards.map((card) => (
          <Box key={card.title} sx={{ width: '100%', display: 'flex' }}>
            <ButtonBase
              aria-pressed={selectedStatus === card.status}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'stretch',
                textAlign: 'start', // 
                borderRadius: 1,
                overflow: 'hidden'
              }}
              onClick={() => {
                // Set the selected status to filter cases
                setSelectedStatus(card.status);
              }}
            >
              <Card
                sx={{
                  borderRadius: 1,
                  boxShadow: 'none',
                  width: '100%',
                  height: '100%',
                  minHeight: 148,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: selectedStatus === card.status ? card.color : 'divider',
                  backgroundColor: selectedStatus === card.status ? alpha(card.color, 0.04) : 'background.paper',
                  transition: 'border-color 150ms ease-in-out, background-color 150ms ease-in-out',
                  '&:hover': {
                    borderColor: card.color,
                    cursor: 'pointer'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: card.color,
                          mb: 0.5,
                          fontSize: '1.5rem',
                          lineHeight: 1.2
                        }}
                      >
                        {card.count}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(card.color, 0.08),
                        color: card.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, lineHeight: 1.4 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.5, minHeight: '2.4em' }}>
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </ButtonBase>
          </Box>
        ))}
      </Box>

      {/* Total Cases Card */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden'
        }}
      >

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                mr: 1.5
              }}
            >
              <AccessTimeOutlined fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 0 }}>
                {t('dashboard.totalCases')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.totalCasesDesc')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h4"
              fontWeight="700"
              color="primary.main"
              sx={{
                fontSize: '2rem',
                lineHeight: 1.1
              }}
            >
              {counts.total || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight="500">
              {t('dashboard.total')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <CasesTable initialStatusFilter={selectedStatus} />
      </Box>
    </Box>
  );
};




export default Dashboard;
